import React, { useMemo } from 'react';
import ReactDom from 'react-dom';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import cx from 'classnames';
import { formattedPathKey } from 'helpers/formattedPathKey';

import { get, getAvailablePages, getConfigProp, SiteConfigManager } from '@atlas/core';
import {
  availableClientProfileRoutes,
  CLIENT_PROFILE_COMPONENT_TYPES,
  clientProfileRoutes,
  COMPONENT_TYPES,
} from '@atlas/routing';
import { Icon, Label, ScrollableContainer } from '@atlas/ui';

import BonusesRouter from 'components/ClientProfile/Bonuses/BonusesRouter';
import bonusesTabRoutes, {
  BONUSES_FREEBETS_SEGMENT,
  BONUSES_HISTORY_SEGMENT,
} from 'components/ClientProfile/Bonuses/bonusesTabRoutes';
import ProfileTabs from 'components/ClientProfile/Bonuses/ProfileTabs';
import ShortFooter from 'components/Footer/ShortFooter';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { BONUSES, BONUSES_BONUS_HISTORY, BONUSES_FREEBET, BONUSES_FREESPINS } =
  CLIENT_PROFILE_COMPONENT_TYPES;
const { bonuses } = clientProfileRoutes;

// the combined feed IS the bonuses page (lives at the parent `bonuses` path with no extra segment), so the BONUSES_DEPOSIT tab is not rendered as a separate nav item
// History and Freespins also stay hidden from the nav; their URLs still resolve for deep links via BonusesRouter
const HIDDEN_TAB_TYPES = new Set([BONUSES_BONUS_HISTORY, BONUSES_FREESPINS]);

const Bonuses = () => {
  const { pathname, search } = useLocation();
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const clientProfileTabs = get(SiteConfigManager.getComponentOptions(CLIENT_PROFILE), 'tabs');
  const bonusesOptions = clientProfileTabs.find(({ type }) => type === BONUSES);

  const { isExpandedOnMobile, tabs = [] } = bonusesOptions;

  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const hideFreebets = (isSportRadarProject || hideSportBonuses) && !isExpandedOnMobile;

  const activeTabs = useMemo(
    () =>
      tabs.filter(
        tab =>
          tab.isActive &&
          !HIDDEN_TAB_TYPES.has(tab.type) &&
          !(hideFreebets && tab.type === BONUSES_FREEBET),
      ),
    [hideFreebets, tabs],
  );

  const showTabNavigation = !isExpandedOnMobile && activeTabs.length > 1;

  const bonusesPath = `${clientProfilePath}/${bonuses}`;
  const excludedSegments = [BONUSES_FREEBETS_SEGMENT, BONUSES_HISTORY_SEGMENT];
  const isExcluded = excludedSegments.some(segment =>
    pathname.startsWith(`${bonusesPath}/${segment}`),
  );

  const isBonusDetail = pathname.startsWith(`${bonusesPath}/`) && !isExcluded;
  // Keep the active ?filter when returning from a bonus detail so the list chip selection survives.
  const backTo = isBonusDetail ? { pathname: bonusesPath, search } : clientProfilePath;

  const isListView = useRouteMatch({
    exact: true,
    path: [
      bonusesPath,
      `${bonusesPath}/${BONUSES_FREEBETS_SEGMENT}`,
      `${bonusesPath}/${BONUSES_HISTORY_SEGMENT}`,
    ],
  });

  const listViewMessage = isExpandedOnMobile
    ? formattedPathKey(pathname, clientProfilePath)
    : availableClientProfileRoutes[BONUSES].name;
  const goBackMessage = isListView ? listViewMessage : 'back_to_all_bonuses';

  const availableRoutes = useMemo(() => {
    const next = { ...bonusesTabRoutes };
    HIDDEN_TAB_TYPES.forEach(type => {
      delete next[type];
    });
    if (isSportRadarProject || hideSportBonuses) {
      delete next[BONUSES_FREEBET];
    }
    return next;
  }, [isSportRadarProject, hideSportBonuses]);

  const availableTabs = useMemo(
    () => getAvailablePages(availableRoutes, tabs),
    [tabs, availableRoutes],
  );

  const className = cx(
    'bonus-history-content-to-scroll',
    isExpandedOnMobile && 'disable-top-navigation',
    !isExpandedOnMobile && !isBonusDetail && 'freebets-enable-expanded',
  );

  return ReactDom.createPortal(
    <div className="profile-wrapper-absolute">
      <Link className="back-to-container" to={backTo}>
        <Icon className="icon-back" name="back" />
        <Label className="title-text" message={goBackMessage} />
      </Link>

      {isListView && showTabNavigation && (
        <div className="profile__tabs">
          <ProfileTabs parentPath={bonusesPath} tabs={availableTabs} />
        </div>
      )}
      <ScrollableContainer className={className} id="content">
        <BonusesRouter />
        <ShortFooter />
      </ScrollableContainer>
    </div>,
    document.body,
  );
};

export default Bonuses;
